import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import AceJsonEditor from './AceJsonEditor.jsx';
import RendererHelpPanel from './RendererHelpPanel.jsx';
import styles from './DynamicFormBuilder.module.css';

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

// stateKey -> Pg::DynamicForm column (what update_renderers permits).
const COLUMN_NAMES = {
  default: 'default_renderer',
  backoffice: 'backoffice_renderer',
  defaultPdf: 'default_pdf_renderer',
  backofficePdf: 'backoffice_pdf_renderer',
};

const TABS = [
  { key: 'default', label: 'Default', pdf: false },
  { key: 'backoffice', label: 'Backoffice', pdf: false },
  { key: 'defaultPdf', label: 'Default PDF', pdf: true },
  { key: 'backofficePdf', label: 'Backoffice PDF', pdf: true },
];

// Lets admins edit the four Handlebars renderer templates and preview each
// against sample data, rendered server-side through the same Handlebars +
// sanitize path production uses. Saves only the renderer columns (separate form
// + endpoint), so it never writes a new field_definition version.
export default function RendererEditor({
  renderers = {},
  useRendererInPdfs = false,
  sampleData = {},
  previewValues = {},
  renderersAction,
  renderPreviewAction,
}) {
  const [templates, setTemplates] = useState({
    default: renderers.default || '',
    backoffice: renderers.backoffice || '',
    defaultPdf: renderers.defaultPdf || '',
    backofficePdf: renderers.backofficePdf || '',
  });
  const [usePdf, setUsePdf] = useState(!!useRendererInPdfs);
  const [activeTab, setActiveTab] = useState('default');
  const [showHelp, setShowHelp] = useState(true);

  // Sample data the previews render against. Seeds from the live preview output
  // when present, else the form's saved `data`. Held as text so admins can tweak
  // it; `dataError` flags unparseable JSON (preview pauses until it's valid).
  const initialData = Object.keys(previewValues || {}).length ? previewValues : (sampleData || {});
  const [dataDraft, setDataDraft] = useState(JSON.stringify(initialData, null, 2));
  const [dataNonce, setDataNonce] = useState(0);
  const [dataError, setDataError] = useState(null);

  const parsedData = useMemo(() => {
    try {
      const value = JSON.parse(dataDraft || '{}');
      return { value, error: null };
    } catch (err) {
      return { value: null, error: err.message };
    }
  }, [dataDraft]);

  useEffect(() => setDataError(parsedData.error), [parsedData.error]);

  // Hide the PDF tabs (and bounce off them) when PDFs don't use the renderer,
  // mirroring the legacy #pdf_renderers toggle.
  const visibleTabs = useMemo(() => TABS.filter((t) => usePdf || !t.pdf), [usePdf]);
  useEffect(() => {
    if (!usePdf && TABS.find((t) => t.key === activeTab)?.pdf) setActiveTab('default');
  }, [usePdf, activeTab]);

  const onTemplateChange = useCallback((text) => {
    setTemplates((prev) => ({ ...prev, [activeTab]: text }));
  }, [activeTab]);

  const useCurrentPreviewValues = useCallback(() => {
    setDataDraft(JSON.stringify(previewValues || {}, null, 2));
    setDataNonce((n) => n + 1);
  }, [previewValues]);

  // Debounced server render of the active template against the sample data.
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewError, setPreviewError] = useState(null);
  const requestId = useRef(0);
  const activeTemplate = templates[activeTab];

  useEffect(() => {
    if (parsedData.error) return undefined;
    const id = ++requestId.current;
    const timer = setTimeout(() => {
      fetch(renderPreviewAction, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({ template: activeTemplate, data: parsedData.value }),
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
        .then(({ html }) => {
          if (id === requestId.current) { setPreviewHtml(html); setPreviewError(null); }
        })
        .catch((err) => {
          if (id === requestId.current) setPreviewError(err.message);
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [activeTemplate, parsedData.value, parsedData.error, renderPreviewAction]);

  const activeIsPdf = TABS.find((t) => t.key === activeTab)?.pdf;

  return (
    <section className="d-flex flex-column gap-2 p-3 bg-white border rounded" aria-label="Renderers">
      <form method="post" action={renderersAction} className="d-flex flex-column gap-3">
        <input type="hidden" name="_method" value="patch" />
        <input type="hidden" name="authenticity_token" value={csrfToken()} />
        <input type="hidden" name="pg_dynamic_form[use_renderer_in_pdfs]" value={usePdf ? '1' : '0'} />
        {Object.entries(COLUMN_NAMES).map(([stateKey, column]) => (
          <input key={column} type="hidden" name={`pg_dynamic_form[${column}]`} value={templates[stateKey]} />
        ))}

        <div className="form-check">
          <input
            id="renderer-use-in-pdfs"
            className="form-check-input"
            type="checkbox"
            checked={usePdf}
            onChange={(e) => setUsePdf(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="renderer-use-in-pdfs">
            Use the renderer in PDFs
          </label>
        </div>

        <ul className="nav nav-tabs" role="tablist">
          {visibleTabs.map((tab) => (
            <li className="nav-item" key={tab.key} role="presentation">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                className={`nav-link ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        <div className={showHelp ? styles.jsonLayout : undefined}>
          <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-baseline gap-2">
                <h3 className="m-0 text-uppercase text-muted small">Template</h3>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowHelp((v) => !v)}>
                  {showHelp ? 'Hide cheat sheet' : 'Show cheat sheet'}
                </button>
              </div>
              <AceJsonEditor
                key={activeTab}
                mode="html"
                value={templates[activeTab]}
                onChange={onTemplateChange}
                ariaLabel={`${activeTab} renderer template`}
              />
              {activeIsPdf && (
                <p className="small text-muted mb-0">
                  PDF previews are an HTML approximation. Use the form&apos;s “Preview PDF” download for true PDF fidelity after saving.
                </p>
              )}
            </div>

            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-baseline gap-2">
                <h3 className="m-0 text-uppercase text-muted small">Sample data</h3>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={useCurrentPreviewValues}>
                  Use current preview values
                </button>
              </div>
              <AceJsonEditor
                key={`data-${dataNonce}`}
                mode="json"
                value={dataDraft}
                onChange={setDataDraft}
                ariaLabel="Renderer sample data"
              />
              {dataError && (
                <div className="alert alert-warning mb-0 py-1 px-2 small" role="alert">
                  Invalid JSON — {dataError}
                </div>
              )}
            </div>

            <div className="d-flex flex-column gap-1" aria-label="Renderer preview">
              <h3 className="m-0 text-uppercase text-muted small">Preview</h3>
              {previewError && (
                <div className="alert alert-warning mb-0 py-1 px-2 small" role="alert">
                  Preview failed — {previewError}
                </div>
              )}
              {/* Server-sanitized HTML (DynamicFormHelper#render_handlebars_template). */}
              <div className="border rounded p-3" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
          {showHelp && <RendererHelpPanel />}
        </div>

        <div className="d-flex justify-content-end">
          <button type="submit" className="btn btn-success">Save renderers</button>
        </div>
      </form>
    </section>
  );
}
